/**
 * Cross-subdomain org-switch handoff.
 *
 * When the team switcher initiates an org swap, it calls `markOrgSwitch`
 * to stash the target org's identity in a short-lived cross-subdomain
 * cookie + sessionStorage copy. The destination page reads this via
 * `readOrgTransition` and renders a branded overlay until the org
 * context catches up — masking the redirect / SSR gap that otherwise
 * flashes old chrome.
 *
 * The window event lets an already-mounted overlay (on the originating
 * page, before navigation) render synchronously without waiting for a
 * cookie read.
 */

import { deleteCookie, getCookie, setCookie } from '$lib/utils/cookies';
import { getCookieDomain } from '$lib/utils/domain';

export type OrgTransition = {
	id: string;
	name: string;
	slug: string | null;
	initials: string;
	logo?: string;
	ts: number;
};

const COOKIE_NAME = 'hessian-org-transition';
const STORAGE_KEY = 'hessian-org-transition';
const COOKIE_MAX_AGE_SECONDS = 30;
const FRESHNESS_MS = 10_000;

export const ORG_TRANSITION_EVENT = 'hessian:org-transition';

/**
 * Mark the start of an org switch. Writes a cookie scoped to the
 * registrable parent domain so the destination subdomain can read it,
 * plus a sessionStorage copy for same-host transitions, and dispatches a
 * window event so an overlay component on the current page can render
 * before the navigation completes.
 */
export function markOrgSwitch(org: {
	id: string;
	name: string;
	slug: string | null;
	initials: string;
	logo?: string;
}): OrgTransition {
	const payload: OrgTransition = { ...org, ts: Date.now() };
	const encoded = JSON.stringify(payload);

	setCookie(COOKIE_NAME, encoded, {
		path: '/',
		domain: getCookieDomain(window.location.hostname),
		sameSite: 'Lax',
		secure: window.location.protocol === 'https:',
		maxAge: COOKIE_MAX_AGE_SECONDS
	});

	try {
		window.sessionStorage.setItem(STORAGE_KEY, encoded);
	} catch {
		// sessionStorage unavailable (rare — incognito quirks, storage disabled).
		// The cookie is the authoritative handoff; the storage copy is just a
		// faster same-host read.
	}

	window.dispatchEvent(new CustomEvent<OrgTransition>(ORG_TRANSITION_EVENT, { detail: payload }));

	return payload;
}

/**
 * Read the current org transition, if one is in flight. Returns null if
 * no transition is set or the transition is stale (>10s old). Stale
 * markers are auto-cleared.
 */
export function readOrgTransition(): OrgTransition | null {
	if (typeof window === 'undefined') return null;

	let raw: string | null = null;

	try {
		raw = window.sessionStorage.getItem(STORAGE_KEY);
	} catch {
		// fall through to cookie
	}

	if (!raw) {
		const cookieValue = getCookie(COOKIE_NAME);
		if (cookieValue) raw = cookieValue;
	}

	if (!raw) return null;

	let parsed: OrgTransition;
	try {
		parsed = JSON.parse(raw) as OrgTransition;
	} catch {
		clearOrgTransition();
		return null;
	}

	if (!parsed?.id || !parsed?.ts || Date.now() - parsed.ts > FRESHNESS_MS) {
		clearOrgTransition();
		return null;
	}

	return parsed;
}

/** Clear the transition marker once the destination has caught up. */
export function clearOrgTransition(): void {
	if (typeof window === 'undefined') return;
	deleteCookie(COOKIE_NAME, {
		path: '/',
		domain: getCookieDomain(window.location.hostname)
	});
	try {
		window.sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		// ignore
	}
}
